/**
 * Converte áudio do browser (webm/wav) para MP3 — formato aceito pelo WhatsApp Cloud / Spark audioId.
 */

type Mp3EncoderCtor = new (channels: number, sampleRate: number, kbps: number) => {
  encodeBuffer(left: Int16Array, right?: Int16Array): Int8Array;
  flush(): Int8Array;
};

let cachedMp3Encoder: Mp3EncoderCtor | null = null;

async function getMp3Encoder(): Promise<Mp3EncoderCtor> {
  if (cachedMp3Encoder) return cachedMp3Encoder;
  const lamejs = await import('lamejs');
  const Mp3Encoder =
    (lamejs as { Mp3Encoder?: Mp3EncoderCtor }).Mp3Encoder ||
    (lamejs as { default?: { Mp3Encoder?: Mp3EncoderCtor } }).default?.Mp3Encoder;
  if (!Mp3Encoder) throw new Error('MP3 encoder unavailable');
  cachedMp3Encoder = Mp3Encoder;
  return Mp3Encoder;
}

async function decodeToAudioBuffer(blob: Blob): Promise<AudioBuffer> {
  const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new AudioCtx();
  try {
    const ab = await blob.arrayBuffer();
    return await ctx.decodeAudioData(ab.slice(0));
  } finally {
    void ctx.close?.();
  }
}

function floatTo16BitPCM(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]!));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

function mixToMono(buffer: AudioBuffer): Float32Array {
  const len = buffer.length;
  const mono = new Float32Array(len);
  const channels = buffer.numberOfChannels;
  for (let c = 0; c < channels; c++) {
    const data = buffer.getChannelData(c);
    for (let i = 0; i < len; i++) mono[i]! += data[i]! / channels;
  }
  return mono;
}

/** Downsample p/ ~22 kHz — encode MP3 bem mais leve na main thread. */
function downsampleMono(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (toRate >= fromRate) return input;
  const ratio = fromRate / toRate;
  const outLen = Math.max(1, Math.floor(input.length / ratio));
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    out[i] = input[Math.min(input.length - 1, Math.floor(i * ratio))]!;
  }
  return out;
}

/** True se o blob já é formato nativo WhatsApp Cloud (audioId). */
export function isWhatsAppNativeAudioMime(mime: string | undefined | null): boolean {
  const m = (mime || '').toLowerCase();
  if (m.includes('webm')) return false;
  return (
    m.includes('mpeg') ||
    m.includes('mp3') ||
    m.includes('ogg') ||
    m.includes('opus') ||
    m.includes('aac') ||
    m.includes('mp4') ||
    m.includes('m4a')
  );
}

/**
 * Garante MP3/OGG/AAC/MP4 para envio Spark. WebM/WAV do MediaRecorder → MP3 via lamejs.
 */
export async function ensureSparkCompatibleAudio(blob: Blob): Promise<{ blob: Blob; mimeType: string; fileName: string }> {
  const mime = (blob.type || '').toLowerCase();
  if (isWhatsAppNativeAudioMime(mime)) {
    const ext = mime.includes('ogg') || mime.includes('opus')
      ? 'ogg'
      : mime.includes('mp4') || mime.includes('m4a')
        ? 'm4a'
        : mime.includes('aac')
          ? 'aac'
          : 'mp3';
    return { blob, mimeType: mime || 'audio/mpeg', fileName: `audio_${Date.now()}.${ext}` };
  }

  const audioBuffer = await decodeToAudioBuffer(blob);
  const targetRate = 22050;
  const mono = downsampleMono(mixToMono(audioBuffer), audioBuffer.sampleRate, targetRate);
  const pcm = floatTo16BitPCM(mono);
  const Mp3Encoder = await getMp3Encoder();
  const encoder = new Mp3Encoder(1, Math.min(audioBuffer.sampleRate, targetRate), 96);
  const block = 1152;
  const parts: BlobPart[] = [];
  for (let i = 0; i < pcm.length; i += block) {
    const slice = pcm.subarray(i, i + block);
    const buf = encoder.encodeBuffer(slice);
    if (buf.length > 0) parts.push(buf);
  }
  const flush = encoder.flush();
  if (flush.length > 0) parts.push(flush);

  const mp3 = new Blob(parts, { type: 'audio/mpeg' });
  if (mp3.size < 32) throw new Error('Failed to encode audio as MP3');
  return { blob: mp3, mimeType: 'audio/mpeg', fileName: `audio_${Date.now()}.mp3` };
}
